<script setup lang="ts">
  import { storeToRefs } from 'pinia';
  import { computed, ref, Ref } from 'vue';

  import BvCallDetails from '@bv/components/voip/call/BvCallDetails.vue';
  import { movable as vMovable, type MovableOptions } from '@bv/directives';
  import { type Call, useSipStore } from '@bv/stores';

  const sipStore = useSipStore();

  const { activeCall, previousCall } = storeToRefs(sipStore);

  export interface Props {
    showExpand?: boolean;
    showTransfer?: boolean;
    hide?: Ref<boolean> | boolean;
  }

  export interface CallWidgetOptions {
    props?: Props;
    events?: Record<string, (...args: any[]) => any>;
  }

  const props = withDefaults(defineProps<Props>(), {
    showTransfer: false,
    showExpand: false,
    hide: () => ref(false),
  });

  const showWidget = computed<boolean>(() => {
    const show: boolean = !((props.hide as Ref)?.value !== false || !props.hide);
    const ringing = !!previousCall.value && activeCall.value?.status === 'ringing';
    return (show || ringing) && !!activeCall.value;
  });

  const emit = defineEmits<{
    (event: 'on-expand'): void;
    (event: 'on-transfer', call: Call): void;
  }>();

  /**
   * Emits an 'on-expand' event.
   * This function is typically used to notify a parent component that an expansion action has been triggered.
   * @returns {void}
   */
  const expand = (): void => {
    emit('on-expand');
  };

  /**
   * Emits an 'on-transfer' event.
   * This function notifies a parent component that the user has initiated a transfer action.
   * @returns {void}
   */
  const transfer = (call: Call): void => {
    emit('on-transfer', call);
  };

  const movableOptions: MovableOptions = { area: '.bv-call-widget-area', position: 'bottom-end', fixed: true };
</script>

<template>
  <div class="bv-call-widget-area fixed inset-4 -z-10" />
  <transition>
    <div
      v-if="showWidget"
      v-movable="movableOptions"
      class="bv-call-widget z-50 h-47 w-82 overflow-hidden rounded-lg"
    >
      <bv-call-details
        action-size="sm"
        class="h-full w-full"
        :show-expand
        :show-transfer
        @on-expand="expand"
        @on-transfer="transfer($event)"
      />
    </div>
  </transition>
</template>

<style lang="scss" scoped>
  .v-enter-active,
  .v-leave-active {
    transition: opacity 0.7s ease;
  }

  .v-enter-from,
  .v-leave-to {
    opacity: 0;
  }

  :deep(.bv-call-details-content) {
    height: 100%;
  }

  :deep(.expandable-header) {
    padding-inline-end: 36px;
  }
</style>
